import {
  ContactAddressLocation,
  OrganisationFeatureFlags,
  StatementLogoPosition,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";
import { BeakerIcon } from "@heroicons/react/20/solid";
import { When } from "react-if";

import { Card } from "../../../../../common/Atoms/Card";
import { CardContent } from "../../../../../common/Atoms/CardContent";
import { Notification } from "../../../../../common/Atoms/Notification";
import { Select } from "../../../../../common/Atoms/Select";
import { useGeneralSettingsForm } from "../../../../hooks/useGeneralSettingsForm";
import { fieldIsValid } from "../../../../lib/helper";
import { SaveAndError } from "../SaveAndError";
import { StatementPreview } from "../StatementPreview";
import { Toggle } from "../../../../../common/Atoms/Toggle";
import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { FormLabel } from "../../../../../common/Atoms/FormLabel";
import { FormDescription } from "../../../../../common/Atoms/FormDescription";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { Transition } from "../../../../../common/Atoms/Transition";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { Divider } from "../../../../../common/Atoms/Divider";
import { DatePicker } from "../../../../../common/Atoms/DatePicker";

import { StatementColumnsEditor } from "./StatementColumnsEditor";

const positionOptions = [
  {
    value: StatementLogoPosition.Right,
    label: `Right (Best for square logos)`,
  },
  {
    value: StatementLogoPosition.Top,
    label: `Top (Best for wide logos)`,
  },
];

const addressLocationOptions = [
  {
    value: ContactAddressLocation.Default,
    label: `Default`,
  },
  {
    value: ContactAddressLocation.Custom,
    label: `Custom (offset for envelope windows)`,
  },
];

/** Settings for the standard statement template. Custom templates set their own in the builder */
export function StatementStandard() {
  const {
    formData,
    onChange,
    hasUnsavedChanges,
    invalids,
  } = useGeneralSettingsForm();

  const currentOrg = useGetSelectedOrganisation();
  const aiStatementBuilderEnabled = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_STATEMENT_BUILDER);

  return (
    <Card>
      <CardContent className={ `pt-6` }>
        <When condition={ aiStatementBuilderEnabled }>
          <Notification
            type={ `info` }
            className={ `mb-6` }
          >
            { `These settings only apply to the standard statement template. Custom templates set their own in the Custom tab.` }
          </Notification>
        </When>

        <div className={ `w-full max-w-[50%] space-y-4` }>
          <Select
            name={ `statementLogoPosition` }
            label={ `Statement Logo Position` }
            description={ `Where your logo is placed on the statement PDF.` }
            options={ positionOptions }
            selected={ formData.statementLogoPosition }
            onChange={ v => onChange(`statementLogoPosition`, v.value) }
            invalid={ fieldIsValid(`statementLogoPosition`, invalids) }
          />

          <div>
            <Select
              name={ `contactAddressLocation` }
              label={ `Contact Address Location` }
              description={ `Shift the recipient name & address block to line up with envelope windows.` }
              options={ addressLocationOptions }
              selected={ formData.contactAddressLocation ?? ContactAddressLocation.Default }
              onChange={ v => onChange(`contactAddressLocation`, v.value) }
              invalid={ fieldIsValid(`contactAddressLocation`, invalids) }
            />
            <Transition
              speed={ `slow` }
              show={ formData.contactAddressLocation === ContactAddressLocation.Custom }
            >
              <div className={ `mt-3 grid grid-cols-1 gap-3` }>
                <InputTextAddon
                  dataType={ `number` }
                  name={ `contactAddressOffsetLeft` }
                  description={ `Offset from left` }
                  value={ formData.contactAddressOffsetLeft ?? `` }
                  onChange={ v => onChange(`contactAddressOffsetLeft`, v.value) }
                  invalid={ fieldIsValid(`contactAddressOffsetLeft`, invalids) }
                  addOnText={ `cm` }
                />
                <InputTextAddon
                  dataType={ `number` }
                  name={ `contactAddressOffsetTop` }
                  description={ `Offset from top` }
                  value={ formData.contactAddressOffsetTop ?? `` }
                  onChange={ v => onChange(`contactAddressOffsetTop`, v.value) }
                  invalid={ fieldIsValid(`contactAddressOffsetTop`, invalids) }
                  addOnText={ `cm` }
                />
              </div>
            </Transition>
          </div>
        </div>

        { /* Statement Column Preferences */ }
        <div className={ `mt-6 mb-0` }>
          <FormLabel>
            { `Statement Column Preferences` }
          </FormLabel>
          <FormDescription>
            { `Choose which columns to show on your PDF statements. Portal columns are set under Portal Settings.` }
          </FormDescription>
        </div>
        <StatementColumnsEditor
          field={ `statementColumns` }
          showTruncate
        />

        <Divider
          text={ `Outstanding Statement Preferences` }
          textSize={ `medium` }
          weight={ `light` }
        />
        { /* Aged Analysis BreakDown */ }
        <Toggle
          label={ `Show Aged Analysis Breakdown` }
          description={ `Show the aged analysis breakdown on the statement & portal.` }
          onChange={ () => onChange(`statementHideAgedBreakdown`, !formData.statementHideAgedBreakdown) }
          checked={ !formData.statementHideAgedBreakdown }
          name={ `statementHideAgedBreakdown` }
        />
        <Transition
          speed={ `slow` }
          show={ !formData.statementHideAgedBreakdown }
        >
          <div>
            <Paragraph
              variant={ `help` }
            >
              { `Adjust the brackets of the aged debts shown on the statement & portal. Clear to remove the breakpoint.` }
            </Paragraph>
            <div className={ `grid grid-cols-3 gap-8` }>
              <InputTextAddon
                dataType={ `number` }
                name={ `agedAnalysisBreakpointOne` }
                description={ `Aged Analysis Breakpoint One` }
                value={ formData.agedAnalysisBreakpointOne || `` }
                onChange={ v => onChange(`agedAnalysisBreakpointOne`, (v.value)) }
                invalid={ fieldIsValid(`agedAnalysisBreakpointOne`, invalids) }
                addOnText={ `days` }
              />

              <InputTextAddon
                dataType={ `number` }
                name={ `agedAnalysisBreakpointTwo` }
                description={ `Aged Analysis Breakpoint Two` }
                value={ formData.agedAnalysisBreakpointTwo || `` }
                onChange={ v => onChange(`agedAnalysisBreakpointTwo`, (v.value)) }
                invalid={ fieldIsValid(`agedAnalysisBreakpointTwo`, invalids) }
                addOnText={ `days` }
                clearable
              />

              <InputTextAddon
                dataType={ `number` }
                name={ `agedAnalysisBreakpointThree` }
                description={ `Aged Analysis Breakpoint Three` }
                value={ formData.agedAnalysisBreakpointThree || `` }
                onChange={ v => onChange(`agedAnalysisBreakpointThree`, (v.value)) }
                invalid={ fieldIsValid(`agedAnalysisBreakpointThree`, invalids) }
                addOnText={ `days` }
                clearable
              />
            </div>
          </div>
        </Transition>

        <div className={ `mt-4` }>
          <Toggle
            label={ `Group statement lines` }
            description={ `Group the statement lines based on the days until due. Also applies to the portal.` }
            name={ `splitStatementOnDue` }
            checked={ formData.splitStatementOnDue || false }
            onChange={ () => onChange(`splitStatementOnDue`, !formData.splitStatementOnDue) }
          />
          <Transition
            speed={ `slow` }
            show={ formData.splitStatementOnDue || false }
          >
            <div>
              <InputTextAddon
                dataType={ `number` }
                name={ `splitStatementOnDueIndex` }
                description={ `Use 0 to split overdue & outstanding on the due date.` }
                value={ formData.splitStatementOnDueIndex }
                onChange={ v => onChange(`splitStatementOnDueIndex`, (v.value)) }
                invalid={ fieldIsValid(`splitStatementOnDueIndex`, invalids) }
                addOnText={ `days overdue` }
              />
            </div>
          </Transition>
        </div>

        <When condition={ accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_ACTIVITY_STATEMENT) }>
          <Divider
            text={ `Activity Statement Preferences` }
            textSize={ `medium` }
            weight={ `light` }
            spacing={ `xs` }

          />
          <Notification
            type={ `pro` }
            icon={ BeakerIcon }
          >
            { `Activity statements are a new feature! - Let us know if you have any feedback.` }
          </Notification>

          <div className={ `flex space-x-6` }>
            <DatePicker
              className={ `min-w-[180px]` }
              label={ `Default period from` }
              onChange={ v => onChange(`activityStartDate`, v.value) }
              name={ `activityStartDate` }
              invalid={ fieldIsValid(`activityStartDate`, invalids) }
              placeholder={ `18 months ago` }
              value={
                formData.activityStartDate === null
                  ? ``
                  : formData.activityStartDate
              }
            />

            <DatePicker
              className={ `min-w-[180px]` }
              label={ `Default period to` }
              onChange={ v => onChange(`activityEndDate`, v.value) }
              name={ `activityEndDate` }
              invalid={ fieldIsValid(`activityEndDate`, invalids) }
              placeholder={ `Today` }
              value={
                formData.activityEndDate === null
                  ? ``
                  : formData.activityEndDate
              }
            />
          </div>
        </When>

        <div className={ `flex justify-end` }>
          <Paragraph
            variant={ `help` }
          >
            { `Logo and payment terms/footer are set under the "general" tab` }
          </Paragraph>
        </div>

        <div className={ `flex justify-end mt-2` }>
          <StatementPreview
            hasUnsavedChanges={ hasUnsavedChanges }
            standard
          />
        </div>
        <SaveAndError
          className={ `mt-2` }
        />
      </CardContent>
    </Card>
  );
}
